import { pick } from 'lodash';
import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';

import { AssetDetail } from 'generated/bffAsset/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';

import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { AssetInfo } from 'models/context/AssetInfo';
import { ClearContextEntityType, ClearContextSuccessActionInputs } from 'models/context/ClearContext';
import { ContextCustomerEntities } from 'models/context/ContextCustomerEntities';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { AuthActionTypes } from 'saga/auth/ducks';
import {
  canClearContextEntityCheck,
  clearContextSuccess,
  ContextActionTypes,
  selectContextCustomerEntities,
  validateContextAction,
} from 'saga/context/ducks';

/* STATE */
export interface ContextAssetState {
  asset: AssetInfo | null;
}

/* ACTION TYPES */
export enum ContextAssetActionTypes {
  SetContextAsset = '@@Context/Asset/SET_CONTEXT_ASSET',
  SetContextAssetSkipped = '@@Context/Asset/SET_CONTEXT_ASSET_SKIPPED',
}

/* ACTIONS */
export const setContextAssetActions = createApiActionCreators(ContextAssetActionTypes.SetContextAsset);
export const setContextAssetSkippedAction = createActionCreator(ContextAssetActionTypes.SetContextAssetSkipped);

/* REDUCERS */
const initialState: ContextAssetState = {
  asset: null,
};

const CLEAR_CONTEXT_RELATED_ENTITIES = [
  ClearContextEntityType.TARIFF_SPACE,
  ClearContextEntityType.CUSTOMER_ACCOUNT,
  ClearContextEntityType.CUSTOMER,
];

const asset = createReducer(initialState.asset, {
  [ContextActionTypes.ClearContext]: {
    [RequestActionTypes.SUCCESS]: (_state: AssetInfo, payload: ClearContextSuccessActionInputs) =>
      canClearContextEntityCheck(payload, ClearContextEntityType.ASSET, CLEAR_CONTEXT_RELATED_ENTITIES) ? null : _state,
    [RequestActionTypes.FAILURE]: (_state: AssetInfo | null) => _state,
  },
  [ContextActionTypes.ValidateContext]: {
    [RequestActionTypes.FAILURE]: (_state: AssetInfo, payload: ContextCustomerEntities) => payload.asset,
  },
  [ContextAssetActionTypes.SetContextAsset]: {
    [RequestActionTypes.SUCCESS]: (_state: AssetInfo | null, payload: AssetDetail) => {
      return pick<AssetInfo>(payload, [
        'assetRefNo',
        'caRefNo',
        'categoryCode',
        'cuRefNo',
        'customName',
        'eternalRefNo',
        'productCode',
        'productCodeLocale',
        'state',
        'tsRefNo',
      ]);
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
  [AuthActionTypes.Logout]: {
    [RequestActionTypes.SUCCESS]: () => null,
  },
});

export default combineReducers<ContextAssetState>({ asset });

/* SELECTORS */
const selectContextAsset = (state: AppState) => state.context.asset || {};

export const selectContextAssetInfo = (state: AppState): AssetInfo | null => selectContextAsset(state).asset;

/* SAGAS */
/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).setContextAsset.setContextAsset
 */
function* setContextAsset({ payload }: any) {
  if (!payload.assetRefNo) {
    return;
  }

  const contextAsset: AssetInfo = yield select(selectContextAssetInfo);

  if (payload.forceUpdate || !contextAsset || contextAsset.assetRefNo !== payload.assetRefNo) {
    const request: ApiClientRequest = {
      apiCall: call(api.getAssetDetail, yield getApiUrl(environment.urlConfig.asset), payload.assetRefNo),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(setContextAssetActions.success(resp.data));
    } else {
      console.log('setContextAsset failure: api.getAssetDetail failed');
      yield put(setContextAssetActions.failure());
    }
  } else {
    console.info(`setContextAsset skipped: contextAsset[assetRefNo=${payload.assetRefNo}] already exists.`);
    yield put(setContextAssetSkippedAction());
  }
}

function* validateContextSetTsOrCaFromAsset(forceUpdate: boolean) {
  const asset: AssetInfo = yield select(selectContextAssetInfo);

  if (asset.tsRefNo) {
    // validateContext: TS_FROM_ASSET
    yield put(
      validateContextAction.request({
        tsRefNo: asset.tsRefNo,
        forceUpdate: forceUpdate,
      })
    );
  } else {
    // validateContext: CA_FROM_ASSET
    yield call(clearContextSuccess, ClearContextEntityType.TARIFF_SPACE, false);
    yield put(
      validateContextAction.request({
        caRefNo: asset.caRefNo,
        forceUpdate: forceUpdate,
      })
    );
  }
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Entities.Session (Context).validateContext.validateContext
 * Part: setContextAsset
 */
function* validateContextSetContextAsset({ payload }: any) {
  if (!payload.assetRefNo) {
    return;
  }

  const contextEntitiesBackup: ContextCustomerEntities = yield select(selectContextCustomerEntities);

  // set context asset
  yield put(
    setContextAssetActions.request({
      assetRefNo: payload.assetRefNo,
      forceUpdate: payload.forceUpdate,
    })
  );

  // waiting for context asset to be set
  yield takeLatest(
    createActionType(ContextAssetActionTypes.SetContextAsset, RequestActionTypes.SUCCESS),
    validateContextSetTsOrCaFromAsset,
    payload.forceUpdate
  );
  yield takeLatest(
    createActionType(ContextAssetActionTypes.SetContextAssetSkipped),
    validateContextSetTsOrCaFromAsset,
    payload.forceUpdate
  );

  yield takeLatest(createActionType(ContextAssetActionTypes.SetContextAsset, RequestActionTypes.FAILURE), function* () {
    // validateContext: set back original context state
    console.log('validateContext failure: SetContextAsset failed - set back original context state');
    yield put(validateContextAction.failure(contextEntitiesBackup));
  });
}

/* EXPORT */
export function* contextAssetSaga() {
  yield takeLatest(
    createActionType(ContextAssetActionTypes.SetContextAsset, RequestActionTypes.REQUEST),
    setContextAsset
  );

  yield takeLatest(
    createActionType(ContextActionTypes.ValidateContext, RequestActionTypes.REQUEST),
    validateContextSetContextAsset
  );
}
